import { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import axiosInstance, { api } from '../../../../services/api';
import { sanitizeCaption, sanitizeHexColor } from '../../../../utils/sanitization';
import { saveGifSticker, removeGifSticker } from '../../../../api/gifApi';
import { showFeatureLockedModal } from '../../../../utils/subscriptionModals';
import {
  DEFAULT_STYLE,
  DEFAULT_TEXT_POSITION,
  DEFAULT_FONT,
  DEFAULT_BACKGROUND_MODE,
  DEFAULT_BACKGROUND_CONFIG,
  DEBOUNCE_GIF_SAVE_MS,
  getInitialStyle,
  getInitialPosition,
  getInitialBackgroundConfig,
} from '../constants/defaults';

/**
 * useImageApprovalState Hook
 *
 * Consolidates all state management for image approval:
 * - Caption text
 * - Style (colors, background)
 * - Text position and alignment
 * - Font selection
 * - Background configuration (for non-9:16 images)
 * - GIF sticker management
 * - Brand colors
 *
 * @param {Object} item - The image item being edited
 * @param {string} selectedAccountId - Current Instagram account ID
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Object} toast - Toast notification handler
 * @param {Function} t - Translation function
 * @returns {Object} State values, setters, and handlers
 */
export function useImageApprovalState(item, selectedAccountId, isOpen, toast, t) {
  // ===========================================================================
  // Caption State
  // ===========================================================================
  const [caption, setCaption] = useState(item?.caption_text || '');
  const [originalCaption, setOriginalCaption] = useState(item?.caption_text || '');

  // ===========================================================================
  // Style State
  // ===========================================================================
  const [style, setStyle] = useState(getInitialStyle(item));
  const [originalStyle, setOriginalStyle] = useState(getInitialStyle(item));

  // ===========================================================================
  // Position State
  // ===========================================================================
  const [textPosition, setTextPosition] = useState(getInitialPosition(item));
  const [originalPosition, setOriginalPosition] = useState(getInitialPosition(item));

  // ===========================================================================
  // Alignment State
  // ===========================================================================
  const [textAlignment, setTextAlignment] = useState(
    item?.text_alignment || item?.caption?.text_alignment || 'left'
  );
  const [originalAlignment, setOriginalAlignment] = useState(
    item?.text_alignment || item?.caption?.text_alignment || 'left'
  );

  // ===========================================================================
  // Font State
  // ===========================================================================
  const [selectedFont, setSelectedFont] = useState(item?.font_name || DEFAULT_FONT);
  const [originalFont, setOriginalFont] = useState(item?.font_name || DEFAULT_FONT);

  // ===========================================================================
  // Background State (for non-9:16 images)
  // ===========================================================================
  const initialBgConfig = getInitialBackgroundConfig(item);
  const [backgroundMode, setBackgroundMode] = useState(initialBgConfig.mode);
  const [backgroundConfig, setBackgroundConfig] = useState({
    backgroundColor: initialBgConfig.backgroundColor,
    gradientType: initialBgConfig.gradientType,
    gradientColors: initialBgConfig.gradientColors,
    gradientAngle: initialBgConfig.gradientAngle,
  });
  const [originalBackgroundConfig, setOriginalBackgroundConfig] = useState(initialBgConfig);

  const hasWrongAspectRatio = item?.background_metadata?.needs_background || false;

  // Computed: canvas background color for LivePreviewCanvas
  const canvasBackgroundColor = backgroundMode === 'solid'
    ? backgroundConfig.backgroundColor
    : null;

  // ===========================================================================
  // GIF Sticker State
  // ===========================================================================
  const [gifMetadata, setGifMetadata] = useState(null);
  const [originalGifMetadata, setOriginalGifMetadata] = useState(null);
  const [showGiphyPanel, setShowGiphyPanel] = useState(false);
  const [isSavingGif, setIsSavingGif] = useState(false);
  const [isRemovingGif, setIsRemovingGif] = useState(false);

  // ===========================================================================
  // Brand Colors State
  // ===========================================================================
  const [brandColors, setBrandColors] = useState(null);

  // ===========================================================================
  // UI State
  // ===========================================================================
  const [isSaving, setIsSaving] = useState(false);

  // ===========================================================================
  // Initialize/Reset State on Item Change
  // ===========================================================================
  useEffect(() => {
    if (item) {
      // Caption
      const initialCaption = item.caption_text || '';
      setCaption(initialCaption);
      setOriginalCaption(initialCaption);

      // Style
      const initialStyle = getInitialStyle(item);
      setStyle(initialStyle);
      setOriginalStyle(initialStyle);

      // Position
      const initialPosition = getInitialPosition(item);
      setTextPosition(initialPosition);
      setOriginalPosition(initialPosition);

      // Alignment
      const initialAlignment = item.text_alignment || item.caption?.text_alignment || 'left';
      setTextAlignment(initialAlignment);
      setOriginalAlignment(initialAlignment);

      // Font
      const initialFont = item.font_name || DEFAULT_FONT;
      setSelectedFont(initialFont);
      setOriginalFont(initialFont);

      // Background
      const initialBgConfig = getInitialBackgroundConfig(item);
      setBackgroundMode(initialBgConfig.mode);
      setBackgroundConfig({
        backgroundColor: initialBgConfig.backgroundColor,
        gradientType: initialBgConfig.gradientType,
        gradientColors: initialBgConfig.gradientColors,
        gradientAngle: initialBgConfig.gradientAngle,
      });
      setOriginalBackgroundConfig(initialBgConfig);

      // GIF Metadata
      const initialGifMetadata = item.GifStickerMetadata
        ? (typeof item.GifStickerMetadata === 'string'
            ? JSON.parse(item.GifStickerMetadata)
            : item.GifStickerMetadata)
        : null;
      setGifMetadata(initialGifMetadata);
      setOriginalGifMetadata(initialGifMetadata);
    }
  }, [item]);

  // ===========================================================================
  // Fetch Brand Colors
  // ===========================================================================
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchBrandSettings = async () => {
      if (!selectedAccountId || !isOpen) return;

      try {
        const settings = await api.getBrandSettings(selectedAccountId, {
          signal: abortController.signal,
        });
        if (isMounted) {
          setBrandColors({
            textColor: settings.text_color || '#FFFFFF',
            backgroundColor: settings.background_color || '#000000',
            textHasBackground: settings.use_background || false,
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          console.error('Failed to load brand colors:', error);
        }
      }
    };

    fetchBrandSettings();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [selectedAccountId, isOpen]);

  // ===========================================================================
  // GIF Auto-Save
  // ===========================================================================
  const debouncedSaveGif = useMemo(
    () => debounce(async (metadata) => {
      if (!item?.image_id || !item?.sentiment || !item?.instagram_account_id) return;

      try {
        await saveGifSticker(item.image_id, metadata, item.sentiment, item.instagram_account_id);
        console.log('[GIF Auto-save] GIF metadata saved successfully');
      } catch (error) {
        console.error('[GIF Auto-save] Failed to save GIF metadata:', error);
      }
    }, DEBOUNCE_GIF_SAVE_MS),
    [item?.image_id, item?.sentiment, item?.instagram_account_id]
  );

  // Auto-save GIF metadata when it changes
  useEffect(() => {
    if (gifMetadata && !isSavingGif && !isRemovingGif && item) {
      debouncedSaveGif(gifMetadata);
    }
  }, [gifMetadata, isSavingGif, isRemovingGif, item, debouncedSaveGif]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSaveGif.cancel();
    };
  }, [debouncedSaveGif]);

  // ===========================================================================
  // Handlers: Caption
  // ===========================================================================
  const handleCaptionChange = useCallback((newCaption) => {
    setCaption(newCaption);
  }, []);

  // ===========================================================================
  // Handlers: Font
  // ===========================================================================
  const handleFontChange = useCallback((fontId) => {
    setSelectedFont(fontId);
  }, []);

  // ===========================================================================
  // Handlers: Style
  // ===========================================================================
  const handleStyleChange = useCallback(async (updates) => {
    const newStyle = { ...style, ...updates };
    setStyle(newStyle);

    // Save style changes to API
    setIsSaving(true);
    try {
      const captionId = item?.caption_id || item?.current_caption_id;
      const sanitizedStyle = {
        ...newStyle,
        textColor: sanitizeHexColor(newStyle.textColor),
        backgroundColor: sanitizeHexColor(newStyle.backgroundColor),
      };

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        caption: { text: sanitizeCaption(caption) },
        style: sanitizedStyle,
        ...(captionId && { previousCaptionId: captionId }),
      });
    } catch (error) {
      console.error('Style save error:', error);
      toast.error(t('modal.toast.styleError'));
    } finally {
      setIsSaving(false);
    }
  }, [caption, style, item, selectedAccountId, toast, t]);

  // ===========================================================================
  // Handlers: Position
  // ===========================================================================
  const handlePositionChange = useCallback(async (newPosition) => {
    setTextPosition(newPosition);

    setIsSaving(true);
    try {
      const captionId = item?.caption_id || item?.current_caption_id;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        style: { textPosition: newPosition },
        ...(captionId && { previousCaptionId: captionId }),
      });
    } catch (error) {
      console.error('Position save error:', error);
      toast.error(t('modal.toast.positionError'));
    } finally {
      setIsSaving(false);
    }
  }, [item, selectedAccountId, toast, t]);

  const handleResetPosition = useCallback(async () => {
    setTextPosition(originalPosition);

    setIsSaving(true);
    try {
      const captionId = item?.caption_id || item?.current_caption_id;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        style: { textPosition: originalPosition },
        resetPosition: true,
        ...(captionId && { previousCaptionId: captionId }),
      });

      toast.success(t('modal.toast.positionReset'));
    } catch (error) {
      console.error('Position reset error:', error);
      toast.error(t('modal.toast.positionResetError'));
    } finally {
      setIsSaving(false);
    }
  }, [originalPosition, item, selectedAccountId, toast, t]);

  // ===========================================================================
  // Handlers: Alignment
  // ===========================================================================
  const handleAlignmentChange = useCallback(async (newAlignment) => {
    setTextAlignment(newAlignment);

    setIsSaving(true);
    try {
      const captionId = item?.caption_id || item?.current_caption_id;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        style: { textAlignment: newAlignment },
        ...(captionId && { previousCaptionId: captionId }),
      });

      toast.success('Textausrichtung gespeichert');
    } catch (error) {
      console.error('Alignment save error:', error);
      toast.error('Fehler beim Speichern der Textausrichtung');
    } finally {
      setIsSaving(false);
    }
  }, [item, selectedAccountId, toast]);

  // ===========================================================================
  // Handlers: Background
  // ===========================================================================
  const handleBackgroundModeChange = useCallback((newMode) => {
    setBackgroundMode(newMode);

    if (newMode === 'gradient' && backgroundConfig.gradientColors.length === 0) {
      setBackgroundConfig((prev) => ({
        ...prev,
        gradientColors: [prev.backgroundColor, '#FFFFFF'],
      }));
    }
  }, [backgroundConfig.gradientColors.length]);

  const handleSolidColorChange = useCallback((newColor) => {
    setBackgroundConfig((prev) => ({
      ...prev,
      backgroundColor: newColor,
    }));
  }, []);

  const handleGradientChange = useCallback((updates) => {
    setBackgroundConfig((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // ===========================================================================
  // Handlers: GIF Sticker
  // ===========================================================================
  const handleSelectGif = useCallback(async (gif) => {
    const previousGif = gifMetadata;
    setGifMetadata(gif);
    setShowGiphyPanel(false);
    setIsSavingGif(true);

    try {
      await saveGifSticker(item.image_id, gif, item.sentiment, item.instagram_account_id);
      toast.success(t('upload:gif.savedSuccess') || 'GIF sticker saved');
    } catch (error) {
      console.error('Failed to save GIF:', error);
      toast.error(t('errors.gifSaveError') || 'Failed to save GIF sticker');
      setGifMetadata(previousGif);
    } finally {
      setIsSavingGif(false);
    }
  }, [gifMetadata, item, toast, t]);

  const handleRemoveGif = useCallback(async () => {
    const previousGif = gifMetadata;
    setIsRemovingGif(true);

    try {
      await removeGifSticker(item.image_id, item.sentiment, item.instagram_account_id);
      setGifMetadata(null);
      toast.success(t('upload:gif.removedSuccess') || 'GIF sticker removed');
    } catch (error) {
      console.error('Failed to remove GIF:', error);
      toast.error(t('errors.gifRemoveError') || 'Failed to remove GIF sticker');
      setGifMetadata(previousGif);
    } finally {
      setIsRemovingGif(false);
    }
  }, [gifMetadata, item, toast, t]);

  const handleOpenGiphyPanel = useCallback(async () => {
    try {
      if (selectedAccountId) {
        const subscriptionInfo = await api.getAccountSubscription(selectedAccountId);
        const hasGifAccess = subscriptionInfo?.features?.includes('gif_rendering');

        if (!hasGifAccess) {
          showFeatureLockedModal({
            feature_id: 'gif_rendering',
            message: t('featureLocked.defaultDescription') ||
              'GIFs in your Stories is not available in your current plan',
          });
          return;
        }
      }

      setShowGiphyPanel(true);
    } catch (error) {
      console.error('Failed to check GIF feature access:', error);
      showFeatureLockedModal({
        feature_id: 'gif_rendering',
        message: t('featureLocked.defaultDescription') ||
          'GIFs in your Stories is not available in your current plan',
      });
    }
  }, [selectedAccountId, t]);

  // ===========================================================================
  // Computed Values
  // ===========================================================================
  const imageUrl = useMemo(() => (
    item?.original_sas_url ||
    item?.caption?.original_sas_url ||
    item?.sas_url ||
    item?.caption?.sas_url ||
    item?.blob_url ||
    item?.blob_path ||
    item?.preview_url ||
    item?.caption?.rendered_sas_url ||
    item?.rendered_sas_url ||
    item?.rendered_blob_path
  ), [item]);

  const textAreaWidth = item?.text_area_width || item?.caption?.text_area_width || 800;
  const textAreaHeight = item?.text_area_height || item?.caption?.text_area_height || 400;
  const fontSize = item?.recommended_font_size || item?.caption?.recommended_font_size || 48;

  // ===========================================================================
  // Return
  // ===========================================================================
  return {
    // Caption
    caption,
    setCaption,
    originalCaption,
    handleCaptionChange,

    // Style
    style,
    originalStyle,
    handleStyleChange,

    // Position
    textPosition,
    originalPosition,
    handlePositionChange,
    handleResetPosition,

    // Alignment
    textAlignment,
    originalAlignment,
    handleAlignmentChange,

    // Font
    selectedFont,
    originalFont,
    handleFontChange,

    // Background
    backgroundMode,
    backgroundConfig,
    originalBackgroundConfig,
    canvasBackgroundColor,
    hasWrongAspectRatio,
    handleBackgroundModeChange,
    handleSolidColorChange,
    handleGradientChange,

    // GIF
    gifMetadata,
    setGifMetadata,
    originalGifMetadata,
    showGiphyPanel,
    setShowGiphyPanel,
    isSavingGif,
    isRemovingGif,
    handleSelectGif,
    handleRemoveGif,
    handleOpenGiphyPanel,

    // Brand Colors
    brandColors,

    // UI State
    isSaving,

    // Computed
    imageUrl,
    textAreaWidth,
    textAreaHeight,
    fontSize,
  };
}

export default useImageApprovalState;
